import InputField from 'components/InputField'
import Switch from 'components/Switch'
import useConfirmation from 'containers/confirmation/useConfirmation'
import useBookmarks from 'containers/embed/useBookmarks'
import UserContext from 'containers/user/userContext'
import React, { useContext, useEffect, useState } from 'react'
import { Bookmark, PopupType } from 'types'
import './Bookmarks.scss'
import PopUpMessage from 'components/PopUpMessage'

const BookmarkSettings = ({ currentOrganization }) => {
  const user = useContext(UserContext)
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([])
  const [isDuplicateError, setIsDuplicateError] = useState(false)
  const [selectedOption, setSelectedOption] = useState<Bookmark>({
    created_by_id: user?.id || undefined,
    id: null,
    name: '',
    state: '',
    created_at: '',
    is_active: true,
    is_default: true,
    is_private: false,
  })
  const [selectedReportSlug, setSelectedReportSlug] = useState('-maya')
  const [popup, setPopup] = useState<PopupType>({
    active: false,
    message: '',
    details: '',
    type: 'success',
    timeout: 3000,
  })
  const { confirm } = useConfirmation()
  const { getBookmarks, deleteBookmark, updateBookmark, bookmarkError } =
    useBookmarks(currentOrganization.id)

  useEffect(() => {
    if (!selectedReportSlug && bookmarks.length > 0) {
      return
    }

    // get bookmarks from local storage
    const existingBookmarks = localStorage.getItem(
      `${currentOrganization.id}-${selectedReportSlug}-bookmarks`
    )
    if (existingBookmarks) {
      setBookmarks(JSON.parse(existingBookmarks))
    }
    try {
      getBookmarks(selectedReportSlug || '').then((bookmarks) => {
        if (bookmarks) {
          // TODO: actually type check
          // @ts-ignore
          setBookmarks(bookmarks)
          localStorage.setItem(
            `${currentOrganization.id}-${selectedReportSlug}-bookmarks`,
            JSON.stringify(bookmarks)
          )
        }
      })
    } catch (error) {
      console.error('Error fetching bookmarks:', error)
    }
  }, [selectedReportSlug])

  useEffect(() => {
    if (bookmarkError) {
      setPopup({
        active: true,
        message: bookmarkError.message,
        type: 'error',
        details: bookmarkError.type,
        timeout: 10000,
      })
    }
  }, [bookmarkError])

  const handleChangeTab = (bookmark) => {
    setSelectedOption(bookmark)
    // scroll to top
    const sidePage = document.querySelector('.side-page')
    if (sidePage) {
      sidePage.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }

  const handleCopyValue = (e, value) => {
    e.preventDefault()
    navigator.clipboard.writeText(value)
  }

  const saveBookmark = async () => {
    if (!selectedReportSlug) {
      alert('invalid report')
      return
    }
    // TODO: save bookmark to db and local storage and change state
    updateBookmark(
      selectedReportSlug,
      selectedOption.id!,
      selectedOption as Bookmark
    )
    setBookmarks((prevBookmarks) =>
      prevBookmarks.map((bookmark) => {
        if (bookmark?.id === selectedOption?.id) {
          return selectedOption
        }
        return bookmark
      })
    )
    setPopup({
      active: true,
      message: `Saved ${selectedOption.name} successfully`,
      type: 'success',
      details: '',
    })
  }

  const handleShareSelectedBookmark = () => {
    let popMessage = {
      active: true,
      message: '',
      type: 'success',
      details: '',
    }
    if (selectedOption?.id) {
      navigator.clipboard.writeText(
        `https://app.mayainsights.com/organization/${currentOrganization.slug}/dashboard/${selectedReportSlug}?bookmark=${selectedOption.id}`
      )
      popMessage = {
        ...popMessage,
        message: `Bookmark ${selectedOption.name} copied to clipboard`,
      }
    } else {
      popMessage = {
        ...popMessage,
        message: `No bookmark selected`,
        type: 'error',
      }
    }
    setPopup(popMessage)
  }

  const handleDeleteBookmark = async (id) => {
    if (!selectedReportSlug) {
      alert('invalid report')
      return
    }

    if (
      !(await confirm(
        `The bookmark "${selectedOption.name}" will be deleted. Are you sure you want to proceed?`
      ))
    ) {
      return
    }

    deleteBookmark(selectedReportSlug, id)
    setBookmarks((prev) => prev.filter((bookmark) => bookmark?.id !== id))
    setSelectedOption({
      created_by_id: user?.id || undefined,
      id: null,
      name: '',
      state: '',
      created_at: '',
      is_active: true,
      is_default: true,
      is_private: false,
    })
  }

  const isDuplicateName = (id: number, value: string) => {
    if (
      bookmarks.find(
        (bookmark) => bookmark.id !== id && bookmark.name === value
      )
    )
      return true
    else return false
  }

  return (
    // <div className="modal settings">
    //   <div className="modal-content">
    <div className="bookmark-settings">
      <div className="pane-containers">
        <div className="options-pane">
          <h2>Bookmarks</h2>
          <ul className="options">
            {bookmarks?.length > 0 ? (
              bookmarks.map((bookmark) => {
                return (
                  <li
                    key={bookmark?.id}
                    onClick={() => handleChangeTab(bookmark)}
                    className={
                      bookmark?.id === selectedOption?.id ? 'selected' : ''
                    }
                  >
                    {bookmark?.name}
                  </li>
                )
              })
            ) : (
              <p>No bookmarks</p>
            )}
          </ul>
        </div>
        <div className="pane-body">
          <div className="pane-header">
            <h2>Bookmark options</h2>
            {selectedOption?.id && (
              <div className="options-container">
                <button
                  className="share tooltip bottom"
                  onClick={handleShareSelectedBookmark}
                  data-tooltip="Share"
                >
                  <span className="material-icons-outlined saved-icon non-selectable">
                    share
                  </span>
                </button>
                <button
                  className="delete"
                  onClick={() => handleDeleteBookmark(selectedOption.id)}
                >
                  Delete
                </button>
              </div>
            )}
          </div>
          {selectedOption?.id ? (
            <>
              <div className="state-option">
                <InputField
                  type="text"
                  label="Bookmark state"
                  disabled={true}
                  value={selectedOption.state}
                ></InputField>
                <button
                  onClick={(e) => handleCopyValue(e, selectedOption.state)}
                  className="copy-button"
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <span className="material-icons-outlined saved-icon non-selectable">
                    copy
                  </span>
                </button>
              </div>
              <div className="state-option">
                <InputField
                  type="text"
                  label="Bookmark name"
                  value={selectedOption.name}
                  error={
                    isDuplicateError
                      ? `"${selectedOption.name}" is taken`
                      : undefined
                  }
                  setValue={(value) => {
                    if (
                      isDuplicateName(selectedOption.id!, value) &&
                      !isDuplicateError
                    ) {
                      setIsDuplicateError(true)
                    } else if (
                      !isDuplicateName(selectedOption.id!, value) &&
                      isDuplicateError
                    ) {
                      setIsDuplicateError(false)
                    }
                    setSelectedOption((prev) => ({ ...prev, name: value }))
                  }}
                ></InputField>
              </div>
              <div className="state-option distance">
                <h3>Visibility:</h3>
                <Switch
                  left="Organization"
                  right="Private"
                  onChange={() =>
                    setSelectedOption((prev) => ({
                      ...prev,
                      is_private: !prev.is_private,
                    }))
                  }
                  checked={selectedOption.is_private}
                />
              </div>
              <div
                className={`state-option ${isDuplicateError ? 'disabled' : ''}`}
              >
                <button
                  className="save"
                  onClick={() => {
                    if (!isDuplicateError) saveBookmark()
                  }}
                  disabled={isDuplicateError}
                >
                  Save
                </button>
              </div>
            </>
          ) : (
            <p>Select a bookmark to view its details</p>
          )}
        </div>
      </div>
      {popup.active && (
        <PopUpMessage
          onClose={() => setPopup((prev) => ({ ...prev, active: false }))}
          message={popup.message}
          type={popup.type}
          details={popup.details}
          time={popup.timeout || 3000}
        />
      )}
    </div>
    //   </div>
    // </div>
  )
}

export default React.memo(BookmarkSettings)
